import React, { useEffect, useState, useRef } from 'react';
import { cn } from '../../lib/utils';
import { Lock } from 'lucide-react';
interface ScrollDialProps {
  value: number;
  onChange: (value: number) => void;
  isLocked?: boolean;
  colorClass?: string;
  label?: string;
}
export function ScrollDial({
  value,
  onChange,
  isLocked,
  colorClass = 'bg-slate-100',
  label
}: ScrollDialProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [scrollPosition, setScrollPosition] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);
  const itemHeight = 60; // Height of each number item
  useEffect(() => {
    if (isOpen && scrollRef.current) {
      // Center the current value
      const targetScroll = (value - 1) * itemHeight;
      scrollRef.current.scrollTop = targetScroll;
      setScrollPosition(targetScroll);
    }
  }, [isOpen, value]);
  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const scrollTop = e.currentTarget.scrollTop;
    setScrollPosition(scrollTop);
    // Snap to nearest value
    const nearestValue = Math.round(scrollTop / itemHeight) + 1;
    if (nearestValue >= 1 && nearestValue <= 9 && nearestValue !== value) {
      onChange(nearestValue);
    }
  };
  const handleScrollEnd = () => {
    if (scrollRef.current) {
      const targetScroll = (value - 1) * itemHeight;
      scrollRef.current.scrollTo({
        top: targetScroll,
        behavior: 'smooth'
      });
    }
  };
  return <div className="relative flex flex-col items-center" data-id="element-144">
      {label && <span className="text-xs font-bold text-slate-400 mb-1 uppercase tracking-wider" data-id="element-145">
          {label}
        </span>}

      <button onClick={() => !isLocked && setIsOpen(!isOpen)} disabled={isLocked} className={cn('w-20 h-20 rounded-2xl flex items-center justify-center text-3xl font-bold shadow-lg transition-all border-4', colorClass, isLocked ? 'opacity-80 cursor-not-allowed border-slate-300' : 'hover:scale-105 active:scale-95 border-blue-400', isOpen && 'ring-4 ring-blue-200 z-20 scale-105')} data-id="element-146">
        {isLocked ? <Lock className="w-6 h-6 text-slate-400" data-id="element-147" /> : <span className="text-slate-800" data-id="element-148">{value}</span>}

        {isLocked && <div className="absolute -top-2 -right-2 w-7 h-7 bg-white rounded-full shadow-md flex items-center justify-center text-sm font-bold border-2 border-slate-200" data-id="element-149">
            {value}
          </div>}
      </button>

      {/* iOS-style Scroll Picker - ABOVE the dial */}
      {isOpen && <>
          <div className="fixed inset-0 z-10" onClick={() => setIsOpen(false)} data-id="element-150" />
          <div className="absolute bottom-full mb-2 z-20 animate-in slide-in-from-bottom-2 duration-200" data-id="element-151">
            <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden" data-id="element-152">
              {/* Selection indicator - 75% transparent */}
              <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-[60px] bg-blue-50/75 border-y-2 border-blue-300/75 pointer-events-none z-10" data-id="element-153" />

              {/* Scrollable numbers */}
              <div ref={scrollRef} onScroll={handleScroll} onScrollEnd={handleScrollEnd} className="h-[180px] overflow-y-scroll scrollbar-hide snap-y snap-mandatory" style={{
            scrollSnapType: 'y mandatory',
            WebkitOverflowScrolling: 'touch'
          }} data-id="element-154">
                {/* Padding top */}
                <div className="h-[60px]" data-id="element-155" />

                {/* Numbers 1-9 */}
                {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(num => {
              const distance = Math.abs(scrollPosition / itemHeight - (num - 1));
              const opacity = Math.max(0.3, 1 - distance * 0.4);
              const scale = Math.max(0.7, 1 - distance * 0.15);
              return <div key={num} className="h-[60px] flex items-center justify-center snap-center transition-all" style={{
                opacity,
                transform: `scale(${scale})`
              }} data-id="element-156">
                      <button onClick={() => {
                  onChange(num);
                  setIsOpen(false);
                }} className={cn('w-16 h-12 rounded-xl font-bold text-2xl transition-colors', value === num ? 'text-blue-600' : 'text-slate-700')} data-id="element-157">
                        {num}
                      </button>
                    </div>;
            })}

                {/* Padding bottom */}
                <div className="h-[60px]" data-id="element-158" />
              </div>
            </div>
          </div>
        </>}
    </div>;
}